import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import dayjs from "dayjs";
import { CustomTextBox } from "components/CustomComponents/CustomTextBox";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { CustomAutoComplete } from "components/CustomComponents";
import { themes } from "services/constants";
import { useForm } from "react-hook-form";
import { useFetch } from "services/hooks/useFetch";
import { useSelector } from "react-redux";
import { onlyNumberRegex } from "services/constants/patterns";
import { showToast } from "services/utils/Status";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { customAPI } from "services/utils/CustomAPI";
import { formKeyText, tooltipStyleProps } from "services/constants/Styles";
import { Info } from "@mui/icons-material";

const advanceType = [
  { id: 1, type: "Business" },
  { id: 2, type: "Personal" },
];

export const Apply = () => {
  const { domain, userDetails, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer
  );
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm();
  const { result: users, setLoad: reCallApi } = useFetch(
    `${domain}list/employee?user_id=${userDetails.id}&company_id=${topbarCompanyId}`
  );
  const today = dayjs().format("YYYY-MM-DD");
  const [descriptions, setDescriptions] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [selectedAdvanceType, setSelectedAdvanceType] = useState("");
  const [buttonLoader, setButtonLoader] = useState(false);
  const [startDate, setStartDate] = useState(today);
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);

  const updateAdvanceType = (event, data) => {
    if (data) {
      setValue("advance_type", data?.label);
      setSelectedAdvanceType(data?.label);
    }
  };
  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
    }
  }, [reCallApi, topbarCompanyId]);

  const onSubmit = async (data) => {
    setButtonLoader(true);
    const response = await customAPI(`${domain}advances/`, "POST", {
      action: "Approved",
      user: data.user_id,
      amount: data.advance_amount,
      repayment_date: data.from_date,
      advance_type: data.advance_type,
      description: data.descriptions,
    });
    if (response.ok) {
      showToast(
        "success",
        `You created ${data.userName} advance on ${data.from_date
          .split("-")
          .reverse()
          .join("-")} and it has been approved.`
      );
      clearAllData();
      setButtonLoader(false);
    } else {
      setButtonLoader(false);
    }
  };

  const clearAllData = () => {
    setDescriptions("");
    setSelectedUserName("");
    setStartDate(today);
    setSelectedAdvanceType("");
    setAdvanceAmount("");
    reset();
  };

  const updateUserName = (event, data) => {
    if (data) {
      setValue("userName", data?.label);
      setValue("user_id", data?.user_id);
      setSelectedUserName(data);
      setShowAutoCompleteAvathar(false);
    } else {
      setValue("userName", null);
      setSelectedUserName(null);
      setShowAutoCompleteAvathar(true);
    }
  };

  const updateAdvanceAmount = (event) => {
    if (
      event.target.value.length <= 8 &&
      onlyNumberRegex.test(event.target.value)
    ) {
      setAdvanceAmount(event.target.value);
      setValue("advance_amount", event.target.value);
    }
  };

  const updateDesciptions = (event) => {
    if (event.target.value.length <= 1024) {
      setValue("descriptions", event.target.value);
      setDescriptions(event.target.value);
    }
  };

  const validateStartDate = (value) => {
    if (!value) {
      return "Date is required.";
    }
    // const selectedDate = new Date(value);
    // const currentDate = new Date();
    // currentDate.setHours(0, 0, 0, 0);
    // if (selectedDate < currentDate) {
    //   return "Date cannot be in the past.";
    // }
    return true;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Mui.Grid
        container
        sx={{
          paddingTop: "10%",
          paddingLeft: "10%",
          paddingRight: "10%",
        }}
      >
        <Mui.Grid
          container
          sx={{
            padding: 1,
            backgroundColor: themes.primary,
            borderRadius: "10px 10px 0 0",
            border: `2px solid ${themes.sidebarDivider}`,
            borderBottom: "none",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Mui.Typography
            sx={{
              fontSize: 24,
              fontWeight: 800,
              font: "bold",
              textAlign: "center",
            }}
          >
            Apply Advance Salary
          </Mui.Typography>
        </Mui.Grid>
        <Mui.Grid
          container
          sx={{
            paddingTop: -1,
            border: `2px solid ${themes.sidebarDivider}`,
            padding: 2,
            borderRadius: "0 0 10px 10px",
          }}
        >
          <Mui.Grid container sx={{ paddingTop: 1 }}>
            <Mui.Grid xs={4}>
              <Mui.Typography sx={formKeyText}>
                Employee Name&nbsp;
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Select the employee applying for advance amount."
                >
                  <Info />
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid xs={0.5}></Mui.Grid>
            <Mui.Grid xs={5}>
              <CustomAutoCompleteWithLogo
                dataList={users?.map((obj) => ({
                  ...obj,
                  label: obj.user_name,
                  image: obj?.image_url,
                }))}
                selectedValue={selectedUserName}
                rules={{
                  ...register("userName", {
                    required: "User name is required",
                  }),
                }}
                updateDataFunction={updateUserName}
                placeHolder="Select the name"
                errorMessage={
                  errors?.userName?.message ? errors?.userName?.message : ""
                }
                textBoxError={errors.userName ? true : false}
                showAvathar={showAutoCompleteAvathar}
                setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
              />
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid container sx={{ paddingTop: 1 }}>
            <Mui.Grid xs={4}>
              <Mui.Typography sx={formKeyText}>
                Advance Type&nbsp;
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Select type of advance (e.g., Personal, Medical)."
                >
                  <Info />
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid xs={0.5}></Mui.Grid>
            <Mui.Grid xs={5}>
              <CustomAutoComplete
                dataList={advanceType?.map((obj) => ({
                  ...obj,
                  label: obj?.type,
                }))}
                rules={{
                  ...register("advance_type", {
                    required: "Advance type is required",
                  }),
                }}
                selectedValue={selectedAdvanceType}
                updateDataFunction={updateAdvanceType}
                placeHolder="Select the advance type"
                errorMessage={
                  errors?.advance_type?.message
                    ? errors?.advance_type?.message
                    : ""
                }
                textBoxError={errors?.advance_type ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid container sx={{ paddingTop: 1 }}>
            <Mui.Grid xs={4}>
              <Mui.Typography sx={formKeyText}>
                Advance Amount&nbsp;
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Enter the amount required."
                >
                  <Info />
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid xs={0.5}></Mui.Grid>
            <Mui.Grid xs={5}>
              <CustomTextBox
                selectedValue={advanceAmount}
                rules={{
                  ...register("advance_amount", {
                    required: "Advance amount is required",
                  }),
                }}
                updateDataFunction={updateAdvanceAmount}
                placeHolder="Enter advance amount"
                errorMessage={
                  errors?.advance_amount?.message
                    ? errors?.advance_amount?.message
                    : ""
                }
                textBoxError={errors.advance_amount ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid container sx={{ paddingTop: 1 }}>
            <Mui.Grid xs={4}>
              <Mui.Typography sx={formKeyText}>
                Payment Date&nbsp;
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Set the expected date of advance salary disbursement."
                >
                  <Info />
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid xs={0.5}></Mui.Grid>
            <Mui.Grid xs={5}>
              <Mui.TextField
                fullWidth
                size="small"
                type="date"
                id="from_date"
                value={startDate}
                {...register("from_date", {
                  required: "Date is required.",
                  validate: validateStartDate,
                })}
                error={errors.from_date}
                helperText={errors.from_date && errors.from_date.message}
                onChange={(event) => {
                  setStartDate(event.target.value);
                }}
              />
            </Mui.Grid>
          </Mui.Grid>

          <Mui.Grid container sx={{ paddingTop: 1 }}>
            <Mui.Grid xs={4}>
              <Mui.Typography sx={formKeyText}>
                Description&nbsp;
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Mention the purpose of requesting an advance."
                >
                  <Info />
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid xs={0.5}></Mui.Grid>
            <Mui.Grid xs={5}>
              <CustomTextBox
                type="multiline"
                selectedValue={descriptions}
                rules={{
                  ...register("descriptions", {
                    required: "Description is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else if (value?.trim().length > 255 && value !== "") {
                          return "Maximum length is 255 characters";
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateDesciptions}
                placeHolder="Enter descriptions"
                errorMessage={
                  errors?.descriptions?.message
                    ? errors?.descriptions?.message
                    : ""
                }
                textBoxError={errors.descriptions ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid
            container
            xs={9.5}
            sx={{
              paddingTop: 2,
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
            spacing={1}
          >
            <Mui.Grid item>
              <CustomButton
                actionName="Clear"
                typeName="button"
                actionFuntion={clearAllData}
              />
            </Mui.Grid>
            <Mui.Grid item>
              <CustomButton
                actionName="Apply"
                typeName="submit"
                disableAction={buttonLoader}
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.Grid>
    </form>
  );
};
